import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/system';
import config from '@configFile';
import ShareButton from '@components/ShareButton';
import PosterDetails from '@components/PosterDetails';
import { getPostPrefix } from '@utils/index';
import MarkdownRenderer from '@components/MarkdownRenderer';
import { CustomBlogPostUser } from '@types';
import { getCommonBackground } from '@pages/Blog/utils';
import ButtonContainer from '@pages/Blog/components/ButtonContainer';
import { AdminBlogActionsBySlug } from '@components/AdminBlogPostActions';
import AuthorBio from '@pages/Blog/components/AuthorBio';
import Title from '@pages/Blog/components/Title';

const CustomBlogPostCard = ({
  customBlogPost,
  permalink,
  isMainContent
}: {
  customBlogPost: CustomBlogPostUser;
  permalink: string;
  isMainContent?: boolean;
}) => {
  const title = `${customBlogPost?.title}`;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box id={`${customBlogPost.slug}`} component="article">
      <Title
        permalink={permalink}
        isMainContent={!!isMainContent}
        link={`/blog/${customBlogPost.slug}`}
        title={`${getPostPrefix()}${title}`}
      />
      <Typography variant="caption" component="div">
        <Box
          sx={getCommonBackground(
            theme.palette.secondaryBackground,
            isDarkMode
          )}
        >
          <PosterDetails
            postDate={new Date(customBlogPost.publishedAt).toISOString()}
          />
        </Box>
      </Typography>
      <Grid
        container
        sx={{
          padding: '1rem 0 0 0',
          position: 'relative'
        }}
      >
        <ButtonContainer>
          <ShareButton
            title={title}
            url={`${config.SITE_URL}/blog/${customBlogPost.slug}`}
          />
          <AdminBlogActionsBySlug slug={customBlogPost.slug} />
        </ButtonContainer>
      </Grid>
      <Box sx={{ margin: '0px 0px' }}>
        <Typography variant="body1" gutterBottom component="div">
          <MarkdownRenderer content={customBlogPost.postText} />
        </Typography>
      </Box>
      {isMainContent ? <AuthorBio /> : null}
    </Box>
  );
};

export default React.memo(CustomBlogPostCard, (prevProps, nextProps) => {
  return (
    prevProps.permalink === nextProps.permalink &&
    prevProps.isMainContent === nextProps.isMainContent &&
    JSON.stringify(prevProps.customBlogPost) ===
      JSON.stringify(nextProps.customBlogPost)
  );
});
